/**
 * /inventory — inventory count list.
 *
 * The page content (hero action buttons, open-session list, sortable
 * history grid that stacks into cards on a phone) is `InventoryCount` from
 * `@ampersandbrands/ui`, shared with Cockpit. This file is the fetch, the
 * HQ store scoping (`?store_no=` or the signed-in user's own store via
 * `useHqStore`), and the PO picker.
 */

import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { InventoryCount as SharedInventoryCount, PoPickerSheet, type InvPoSummary } from '@ampersandbrands/ui';
import { useCreateCountSession, useInventoryCounts, useStorePos } from '@/lib/useInventory';
import { isNotAvailable } from '@/lib/useStores';
import { useHqStore } from '@/lib/useHqStore';

export function InventoryCount() {
  const [params] = useSearchParams();
  const { storeNo: hqStoreNo } = useHqStore();
  const storeNo = params.get('store_no') ? Number(params.get('store_no')) : (hqStoreNo ?? undefined);

  const { data, isLoading, error } = useInventoryCounts(storeNo);
  const navigate = useNavigate();
  const createCount = useCreateCountSession();
  const [pickingPo, setPickingPo] = useState(false);
  const posQ = useStorePos(storeNo, pickingPo);

  const storeQs = storeNo !== undefined ? `?store_no=${storeNo}` : '';

  function startShelfCount() {
    createCount.mutate(
      { kind: 'shelf', ...(storeNo !== undefined ? { store_no: storeNo } : {}) },
      { onSuccess: ({ session }) => navigate(`/inventory/${session.id}${storeQs}`) },
    );
  }

  function handlePoSelect(po: InvPoSummary) {
    createCount.mutate(
      { kind: 'receiving', po_no: po.po_no, ...(storeNo !== undefined ? { store_no: storeNo } : {}) },
      { onSuccess: ({ session }) => navigate(`/inventory/${session.id}${storeQs}`) },
    );
  }

  const notAvailable = isNotAvailable(error);

  return (
    <div className="mx-auto w-full max-w-4xl pb-24">
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-candy-ink dark:text-candy-dark-ink">Inventory Counts</h1>
      </div>

      {notAvailable ? (
        <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
          Inventory counts are not available yet.
        </p>
      ) : (
        <SharedInventoryCount
          sessions={data?.sessions ?? []}
          loading={isLoading}
          starting={createCount.isPending}
          startError={
            createCount.isError ? (
              <p className="mb-3 text-sm text-candy-pink dark:text-candy-pink-dark">
                Could not start a count. Please try again.
              </p>
            ) : undefined
          }
          onStartShelf={startShelfCount}
          onStartReceiving={() => setPickingPo(true)}
          onOpenSession={(s) => navigate(`/inventory/${s.id}${storeQs}`)}
          footer={
            <Link to="/inventory/variance" className="text-sm text-candy-teal hover:underline dark:text-candy-teal-dark">
              View variance report &rarr;
            </Link>
          }
        />
      )}

      <PoPickerSheet
        open={pickingPo}
        pos={posQ.data?.pos ?? []}
        loading={posQ.isLoading}
        emptyMessage="No open purchase orders found."
        onPick={(po) => {
          setPickingPo(false);
          handlePoSelect(po);
        }}
        onClose={() => setPickingPo(false)}
      />
    </div>
  );
}
